import React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { Truck } from "../../../types";
import { colors } from "../../../utils/colors";
import { formatDateTime } from "../../../utils/dateUtils";
import { ActionMenu } from "../../ui/ActionMenu";
import { ImgDisplay } from "./ImgDisplay";

interface VehicleHistoryTableProps {
  trucks: Truck[];
  token?: string;
  updateEntryDirection: (truck: Truck, direction: "in" | "out") => void;
  updateTruck?: (truck: Truck) => void;
  updateLicensePlate?: (truck: Truck, newLicensePlate: string) => void;
  deleteTruck?: (truck: Truck) => void;
  groupedTrucks?: Record<string, Truck[]>;
}

export function VehicleHistoryTable({
  trucks,
  token,
  updateEntryDirection,
  updateTruck,
  updateLicensePlate,
  deleteTruck,
  groupedTrucks,
}: VehicleHistoryTableProps): React.JSX.Element {
  if (!trucks || trucks.length === 0) {
    return (
      <Box
        sx={{
          p: 3,
          textAlign: "center",
          color: colors.secondaryText,
          fontFamily: "SigmaSans",
        }}
      >
        No vehicle data available
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
      <Table aria-label="vehicle history table">
        <TableHead sx={{ backgroundColor: colors.background }}>
          <TableRow>
            <TableCell
              style={{
                fontFamily: "SigmaSans",
                fontWeight: "bold",
                width: "25%",
                padding: "16px",
              }}
            >
              IMAGE
            </TableCell>
            <TableCell
              style={{
                fontFamily: "SigmaSans",
                fontWeight: "bold",
                width: "25%",
              }}
            >
              TIMESTAMP
            </TableCell>
            <TableCell
              style={{
                fontFamily: "SigmaSans",
                fontWeight: "bold",
                width: "25%",
              }}
            >
              DIRECTION
            </TableCell>
            <TableCell
              style={{
                fontFamily: "SigmaSans",
                fontWeight: "bold",
                width: "12.5%",
              }}
            ></TableCell>
            <TableCell
              style={{
                fontFamily: "SigmaSans",
                fontWeight: "bold",
                width: "12.5%",
              }}
            ></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {trucks.map((truck) => (
            <TableRow
              key={truck.id}
              sx={{
                backgroundColor: colors.cardBackground,
                "&:nth-of-type(even)": {
                  backgroundColor: colors.background,
                },
                "&:nth-of-type(odd)": {
                  backgroundColor: colors.cardBackground,
                },
              }}
            >
              <TableCell style={{ width: "20%" }}>
                <ImgDisplay path={truck.imagepath} height={56} />
              </TableCell>
              <TableCell style={{ width: "20%", fontFamily: "SigmaSans" }}>
                {formatDateTime(truck.intime)}
              </TableCell>
              <TableCell style={{ width: "20%", fontFamily: "SigmaSans" }}>
                {truck.direction || "-"}
              </TableCell>
              <TableCell
                style={{
                  width: "20%",
                  fontFamily: "SigmaSans",
                  color: colors.error,
                  fontWeight: "bold",
                }}
              >
                {truck.has_adr ? "ADR" : ""}
              </TableCell>
              <TableCell style={{ width: "20%", textAlign: "right" }}>
                <ActionMenu
                  truck={truck}
                  token={token}
                  onDirectionChange={updateEntryDirection}
                  onLicensePlateChange={(truck, newLicensePlate) => {
                    if (updateLicensePlate) {
                      updateLicensePlate(truck, newLicensePlate);
                    } else if (updateTruck) {
                      const updatedTruck = {
                        ...truck,
                        registration: newLicensePlate,
                      };
                      updateTruck(updatedTruck);
                    }
                  }}
                  onDelete={(truck) => {
                    if (deleteTruck) {
                      deleteTruck(truck);
                    }
                  }}
                  groupedTrucks={groupedTrucks}
                  context="details-modal"
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
